const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/zgks/eCheckin/initialValues.ts';
import { usePatientData } from 'App/store/patient';
import { useSelector } from 'react-redux';
import { selectUserDetailsFromRegisterWorkItem } from 'App/store/login';
import { normalizePhoneNumber } from 'App/utils/phoneNumberUtils';
import { useRelatives } from 'App/store/relative';
import { selectRelationTypes } from 'App/store/metaKat';
import { useCaseData } from 'App/store/case';
import { getArztDataFromCase } from 'App/utils/cases';
import {
    SECTION_PATH_EINWARZT,
    SECTION_PATH_HAUSARZT,
    SECTION_PATH_KONTAKT,
    SECTION_PATH_PERS_ANGABEN,
} from 'App/config/zgks/eCheckin/constants';
import { useMemo } from 'react';
import { findRelationKey } from 'App/utils/person';
import _ from 'lodash';

export const useECheckinInitialValues = (fallNr?: string) => {
    const [pat, patientIsLoading] = usePatientData();
    const loginDetails = useSelector(selectUserDetailsFromRegisterWorkItem);

    const [caseData, caseDataIsLoading] = fallNr ? useCaseData(fallNr) : [undefined, false];
    const hausArztData = getArztDataFromCase(caseData, 'HausAz');
    const einwArztData = getArztDataFromCase(caseData, 'EinwAz');
    const relationTypes = useSelector(selectRelationTypes);

    const { data: referencePersons, isPendingOrLoading: isRelativesLoading } = fallNr
        ? useRelatives(fallNr)
        : { data: undefined, isPendingOrLoading: false };

    const isLoading = patientIsLoading || caseDataIsLoading || isRelativesLoading;

    const data = useMemo(() => {
        if (isLoading) return {};

        const persAngaben = {
            firstname: pat.get('VORNAME') ?? loginDetails?.get('firstname') ?? '',
            lastname: pat.get('NAME') ?? loginDetails?.get('lastname') ?? '',
            streetAndNumber: pat.get('STRASSE') ?? '',
            plz: pat.get('PLZ') ?? '',
            ort: pat.get('ORT') ?? '',
            country: pat.get('LAND') ?? '',
            geschlecht: pat.get('SEX') ?? '',
            dateOfBirth: (pat.get('GEBDAT') || '').slice(0, 10), // YYYY-MM-DD
            tel: normalizePhoneNumber(pat.get('TELEFON') || pat.get('TELEFON_G') || ''),
            mobile: normalizePhoneNumber(pat.get('TELEFON_M') ?? loginDetails?.get('mobile') ?? ''),
            nationality: pat.get('NATIONALITAET')?.toUpperCase() ?? '',
            zivilstand: pat.get('ZIVILSTAND') ?? '',
            lang: pat.get('SPRACHE') ?? '',
            ahv: (pat.get('SOZVERSNR') as string)?.replace(/\./g, '') ?? '',
            religion: pat.get('KONFESSION') ?? '',
            profession: pat.get('BERUF') ?? '',
            confirmed: pat.get('confirmed'),
            email: loginDetails?.get('email') || pat.get('EMAIL') || '',
            hometown: pat.get('HEIMATORT') || '',
        };

        const hausArzt = hausArztData
            ? {
                  firstname: hausArztData.Vorname,
                  lastname: hausArztData.Name,
                  streetAndNumber: hausArztData.Strasse,
                  tel: normalizePhoneNumber(hausArztData.Tel),
                  ort: hausArztData.Ort,
                  plz: hausArztData.PLZ,
                  country: hausArztData.Land,
              }
            : {};

        const getEinwArzt = () => {
            if (hausArztData && einwArztData && hausArztData.GLN === einwArztData.GLN) {
                return {
                    _einweisungType: 'Hausarzt',
                    hausArzt: '1',
                };
            } else if (einwArztData) {
                return {
                    _einweisungType: 'andere',
                    firstname: einwArztData.Vorname,
                    lastname: einwArztData.Name,
                    streetAndNumber: einwArztData.Strasse,
                    tel: normalizePhoneNumber(einwArztData.Tel),
                    ort: einwArztData.Ort,
                    plz: einwArztData.PLZ,
                    country: einwArztData.Land,
                };
            }
            return {};
        };

        const einwArzt = getEinwArzt();

        const refPerson = _.compact(referencePersons)?.[0];

        const kontakt = refPerson
            ? {
                  anrede: refPerson.ANREDE,
                  lastname: refPerson.NAME,
                  firstname: refPerson.VORNAME,
                  country: refPerson.LAND,
                  tel: normalizePhoneNumber(refPerson.TEL),
                  streetAndNumber: refPerson.STRASSE,
                  plz: refPerson.PLZ,
                  ort: refPerson.ORT,
                  beziehung: findRelationKey(relationTypes, refPerson.VERWANDSCHAFTSGRAD),
              }
            : {};

        return {
            [SECTION_PATH_PERS_ANGABEN]: persAngaben,
            [SECTION_PATH_HAUSARZT]: hausArzt,
            [SECTION_PATH_EINWARZT]: einwArzt,
            [SECTION_PATH_KONTAKT]: kontakt,
        };
    }, [isLoading, relationTypes.length]);

    return [data, isLoading];
};
